import React, { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { designStyles } from "../data/mock";
import { ArrowRight } from "lucide-react";

const useInView = (options = {}) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return [ref, inView];
};

const StyleItem = ({ style, index }) => {
  const [ref, inView] = useInView();
  const [isHovered, setIsHovered] = useState(false);
  const isEven = index % 2 === 0;

  return (
    <div
      ref={ref}
      className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center py-16 md:py-24 ${
        index < designStyles.length - 1 ? "border-b border-stone-200" : ""
      }`}
    >
      <div
        className={`${
          isEven ? "lg:order-1" : "lg:order-2"
        } transition-all duration-1000 ${
          inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
        }`}
      >
        <span className="font-display text-7xl md:text-8xl text-stone-200/80 block mb-4">
          {String(index + 1).padStart(2, "0")}
        </span>
        <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-stone-900 mb-3">
          {style.title}
        </h2>
        <p className="font-body text-[#B8977E] text-sm tracking-wide mb-6">
          {style.subtitle}
        </p>
        <p className="font-body text-stone-500 text-base leading-relaxed mb-8 max-w-lg">
          {style.description}
        </p>
        <Link
          to="/catapult/journey"
          className="inline-flex items-center gap-3 font-body text-sm tracking-[0.08em] uppercase text-stone-900 group"
        >
          Explore this style
          <ArrowRight size={16} className="group-hover:translate-x-2 transition-transform duration-300" />
        </Link>
      </div>
      <div
        className={`${
          isEven ? "lg:order-2" : "lg:order-1"
        } transition-all duration-1000 delay-200 ${
          inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
        }`}
      >
        <div
          className="relative aspect-[4/3] overflow-hidden group cursor-pointer"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <img
            src={style.image}
            alt={style.title}
            className={`w-full h-full object-cover transition-transform duration-700 ${
              isHovered ? "scale-105" : "scale-100"
            }`}
          />
        </div>
      </div>
    </div>
  );
};

export default function DesignStylesPage() {
  const [heroRef, heroInView] = useInView();

  return (
    <main>
      <section
        ref={heroRef}
        className="pt-32 md:pt-44 pb-16 md:pb-20 px-6 md:px-12 bg-[#FAFAF7]"
      >
        <div className="max-w-[1400px] mx-auto">
          <span
            className={`font-body text-[11px] tracking-[0.3em] uppercase text-[#B8977E] mb-6 block transition-all duration-1000 ${
              heroInView ? "opacity-100" : "opacity-0"
            }`}
          >
            Design Styles
          </span>
          <h1
            className={`font-display text-5xl md:text-6xl lg:text-7xl text-stone-900 leading-[1.05] mb-8 max-w-3xl transition-all duration-1000 delay-200 ${
              heroInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            Every home tells a different story.
          </h1>
          <p
            className={`font-body text-stone-500 text-lg leading-relaxed max-w-2xl transition-all duration-1000 delay-400 ${
              heroInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            Whether you're drawn to sleek modern lines or the warmth of a farmhouse,
            we bring expertise and artistry to every style.
          </p>
        </div>
      </section>

      <section className="pb-28 md:pb-40 px-6 md:px-12 bg-[#FAFAF7]">
        <div className="max-w-[1400px] mx-auto space-y-0">
          {designStyles.map((style, i) => (
            <StyleItem key={style.id} style={style} index={i} />
          ))}
        </div>
      </section>
    </main>
  );
}
